import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Building, Mail, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReferralDetail = () => {
  const { referralId } = useParams();
  const navigate = useNavigate();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralDetail();
  }, [referralId]);

  const fetchReferralDetail = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('leadboard_session_token');
      const response = await fetch(`${BACKEND_URL}/api/v1/admin/referrals?referral_id=${referralId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.referrals && data.referrals.length > 0) {
          setReferral(data.referrals[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching referral:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !referral) {
    return <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
      <p className="text-[#4C5A70]">{loading ? 'Loading...' : 'Referral not found'}</p>
    </div>;
  }

  return (
    <div className="min-h-screen bg-[#F7F6F2] py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        <Button data-testid="referral-detail-button-1" asChild variant="ghost" className="mb-6 text-[#0B2240]">
          <Link data-testid="referral-detail-link-1" to="/leadboard"><ArrowLeft className="w-4 h-4 mr-2" />Back to LeadBoard</Link>
        </Button>

        <div className="flex justify-between items-start mb-6">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#0B2240]">
            Referral Details
          </h1>
          <Badge className={
            referral.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
            referral.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
            referral.status === 'qualified' ? 'bg-green-100 text-green-800' :
            referral.status === 'converted' ? 'bg-purple-100 text-purple-800' :
            'bg-gray-100 text-gray-800'
          }>
            {referral.status.toUpperCase()}
          </Badge>
        </div>

        <div className="space-y-6">
          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl">Referrer Information</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Name</p>
                  <p className="font-medium text-[#0B2240]">{referral.referrer_name}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Email</p>
                  <p className="font-medium text-[#0B2240]">{referral.referrer_email}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#E1DFD7]">
            <CardHeader className="border-b border-[#E1DFD7]">
              <CardTitle className="text-xl flex items-center gap-2">
                {referral.lead_type === 'business' ? <Building className="w-5 h-5 text-[#D4AF37]" /> : <User className="w-5 h-5 text-[#D4AF37]" />}
                Referee Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#4C5A70] mb-1">Name</p>
                  <p className="font-medium text-[#0B2240]">{referral.referee_name}</p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Type</p>
                  <p className="font-medium text-[#0B2240]">
                    {referral.lead_type === 'business' ? 'Business Owner' : 'Individual/Family'}
                  </p>
                </div>
                <div>
                  <p className="text-[#4C5A70] mb-1">Email</p>
                  <p className="font-medium text-[#0B2240]">{referral.referee_email}</p>
                </div>
                {referral.referee_phone && (
                  <div>
                    <p className="text-[#4C5A70] mb-1">Phone</p>
                    <p className="font-medium text-[#0B2240]">{referral.referee_phone}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ReferralDetail;
